import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, IconButton, Divider } from "@mui/material";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCreateAgency } from "../../hooks/agencies/useCreateAgency";
import { useUpdateAgency } from "../../hooks/agencies/useUpdateAgency";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";

const FormAddEditAgency = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    // Basic Fields
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [subTitleAr, setSubTitleAr] = useState("");
    const [subTitleEn, setSubTitleEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [image, setImage] = useState(null);
    const [slug, setSlug] = useState("");
    const [sortOrder, setSortOrder] = useState(0);
    const [isActive, setIsActive] = useState(1);
    const [showInHome, setShowInHome] = useState(1);

    // Products Section Fields
    const [productsSectionTitleAr, setProductsSectionTitleAr] = useState("");
    const [productsSectionTitleEn, setProductsSectionTitleEn] = useState("");
    const [productsSectionDescriptionAr, setProductsSectionDescriptionAr] = useState("");
    const [productsSectionDescriptionEn, setProductsSectionDescriptionEn] = useState("");

    // Features (dynamic array)
    const [features, setFeatures] = useState([]);

    // Products (existing from server in edit mode: [{id, url}])
    const [products, setProducts] = useState([]);

    // New products uploaded via MultiImageUploader
    const [newProducts, setNewProducts] = useState([]);
    console.log("🚀 ~ FormAddEditAgency ~ newProducts:", newProducts)

    // Deleted product image IDs (for update only)
    const [deletedImages, setDeletedImages] = useState([]);

    // Hooks
    const { createAgencyMutate, isPending: isCreating } = useCreateAgency();
    const { updateAgencyMutate, isPending: isUpdating } = useUpdateAgency();

    const loadingSubmit = isCreating || isUpdating;

    // Auto-slug generation from English title
    useEffect(() => {
        if (activeLang === "en" && titleEn) {
            if (!isEdit) {
                setSlug(slugify(titleEn));
            } else {
                const originalTitleEn = currentData?.title?.en || "";
                if (titleEn !== originalTitleEn) {
                    setSlug(slugify(titleEn));
                }
            }
        }
    }, [titleEn, activeLang, isEdit, currentData]);

    // Populate data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || "");
            setTitleEn(currentData?.title?.en || "");
            setSubTitleAr(currentData?.sub_title?.ar || "");
            setSubTitleEn(currentData?.sub_title?.en || "");
            setDescriptionAr(currentData?.description?.ar || "");
            setDescriptionEn(currentData?.description?.en || "");
            setSlug(currentData?.slug || "");
            setSortOrder(currentData?.sort_order || 0);
            setIsActive(currentData?.is_active ?? 1);
            setShowInHome(currentData?.show_in_home ?? 1);
            setImage(currentData?.image || null);

            // Products Section
            setProductsSectionTitleAr(currentData?.products_section_title?.ar || "");
            setProductsSectionTitleEn(currentData?.products_section_title?.en || "");
            setProductsSectionDescriptionAr(currentData?.products_section_description?.ar || "");
            setProductsSectionDescriptionEn(currentData?.products_section_description?.en || "");

            // Features
            if (currentData?.features && Array.isArray(currentData.features)) {
                setFeatures(currentData.features.map(f => ({
                    image: f.image || "",
                    titleAr: f.title?.ar || "",
                    titleEn: f.title?.en || "",
                    descriptionAr: f.description?.ar || "",
                    descriptionEn: f.description?.en || "",
                })));
            }

            // Products - comes as [{id, url}] from server
            if (currentData?.products && Array.isArray(currentData.products)) {
                setProducts(currentData.products);
            }
        }
    }, [isEdit, currentData]);

    const handleSlugChange = (e) => {
        const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
        setSlug(val);
    };

    const handleImageChange = (e) => {
        const val = e.target.value;
        setImage(val);
    };

    const getImageName = (imageVal) => {
        if (!imageVal) return "";
        if (typeof imageVal === 'string') {
            return imageVal.substr(imageVal.lastIndexOf('/') + 1);
        }
        if (imageVal?.file_name) return imageVal.file_name;
        if (imageVal?.name) return imageVal.name;
        return imageVal;
    };

    // ========== Features Handlers ==========
    const addFeature = () => {
        setFeatures(prev => [...prev, {
            image: "",
            titleAr: "",
            titleEn: "",
            descriptionAr: "",
            descriptionEn: "",
        }]);
    };

    const removeFeature = (index) => {
        setFeatures(prev => prev.filter((_, i) => i !== index));
    };

    const updateFeature = (index, field, value) => {
        setFeatures(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
    };

    const handleFeatureImageChange = (index, e) => {
        const val = e.target.value;
        updateFeature(index, 'image', val);
    };

    // ========== Products Handlers ==========
    const removeProduct = (index) => {
        const product = products[index];
        // If the product has an ID (existing from server), track it for deletion
        if (product && typeof product === 'object' && product.id) {
            setDeletedImages(prev => [...prev, product.id]);
        }
        setProducts(prev => prev.filter((_, i) => i !== index));
    };

    // ========== Submit ==========
    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            title: {
                ar: titleAr || titleEn,
                en: titleEn || titleAr
            },
            sub_title: {
                ar: subTitleAr || subTitleEn,
                en: subTitleEn || subTitleAr
            },
            description: {
                ar: descriptionAr || descriptionEn,
                en: descriptionEn || descriptionAr
            },
            products_section_title: {
                ar: productsSectionTitleAr || productsSectionTitleEn,
                en: productsSectionTitleEn || productsSectionTitleAr
            },
            products_section_description: {
                ar: productsSectionDescriptionAr || productsSectionDescriptionEn,
                en: productsSectionDescriptionEn || productsSectionDescriptionAr
            },
            slug: slug,
            sort_order: Number(sortOrder),
            is_active: Number(isActive),
            show_in_home: Number(showInHome),
        };

        if (image) {
            payload.image = getImageName(image);
        }

        // Features
        if (features.length > 0) {
            payload.features = features.map(f => ({
                image: getImageName(f.image),
                title: {
                    ar: f.titleAr || f.titleEn,
                    en: f.titleEn || f.titleAr,
                },
                description: {
                    ar: f.descriptionAr || f.descriptionEn,
                    en: f.descriptionEn || f.descriptionAr,
                },
            }));
        }

        // Products - existing (minus deleted) + new uploads
        const keptProducts = products.filter(p => typeof p !== 'object' || !deletedImages.includes(p.id));
        const existingFilenames = keptProducts.map(p => {
            if (typeof p === 'string') return p;
            if (p?.url) return getImageName(p.url);
            return getImageName(p);
        });
        const newFilenames = newProducts.map(p => p.file_name || getImageName(p.url)).filter(Boolean);
        payload.products = [...existingFilenames, ...newFilenames];

        // Deleted images (only in update mode)
        if (isEdit && deletedImages.length > 0) {
            payload.deleted_images = deletedImages;
        }

        // Send
        if (isEdit) {
            updateAgencyMutate({ id: currentData.id, data: payload });
        } else {
            createAgencyMutate(payload);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل الوكالة" : "Edit Agency") : (isArabic ? "إضافة وكالة جديدة" : "Add New Agency")}
                    </Typography>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Image Upload */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <FormInput
                            type="file"
                            name="image"
                            labelImage={isArabic ? "صورة الوكالة" : "Agency Image"}
                            value={image}
                            onChange={handleImageChange}
                            width={200}
                            height={120}
                            required={!isEdit}
                        />
                    </Grid>

                    {/* Multilingual Titles */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الرئيسي (عربي)"
                                placeholder="أدخل العنوان الرئيسي بالعربي"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Main Title (English)"
                                placeholder="Enter main title in English"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Multilingual Sub Titles */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الفرعي (عربي)"
                                placeholder="أدخل العنوان الفرعي بالعربي"
                                value={subTitleAr}
                                onChange={(e) => setSubTitleAr(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Sub Title (English)"
                                placeholder="Enter sub title in English"
                                value={subTitleEn}
                                onChange={(e) => setSubTitleEn(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Slug Field */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug - إنجليزي فقط)" : "Slug (English only)"}
                            placeholder="Slug"
                            type="text"
                            value={slug}
                            onChange={handleSlugChange}
                            disabled={activeLang === "en"}
                            required
                            helperText={activeLang === "ar" ? (isArabic ? "يجب إدخال الرابط باللغة الإنجليزية" : "Slug must be entered in English") : ""} />
                    </Grid>

                    {/* Sort Order Field */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="number"
                            label={isArabic ? "ترتيب العرض" : "Sort Order"}
                            placeholder="0"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Is Active Switch */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="switch"
                            label={isArabic ? "الحالة (مفعّل/معطّل)" : "Status (Active/Inactive)"}
                            name="is_active"
                            checked={!!isActive}
                            onChange={(e) => setIsActive(e.target.checked ? 1 : 0)}
                        />
                    </Grid>

                    {/* Show In Home Switch */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="switch"
                            label={isArabic ? "عرض في الصفحة الرئيسية" : "Show in Home"}
                            name="show_in_home"
                            checked={!!showInHome}
                            onChange={(e) => setShowInHome(e.target.checked ? 1 : 0)}
                        />
                    </Grid>

                    {/* Multilingual Descriptions */}
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                الوصف (عربي)
                            </Typography>
                            <TextEditor
                                name="description_ar"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                placeholder="أدخل الوصف بالعربي"
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Description (English)
                            </Typography>
                            <TextEditor
                                name="description_en"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                placeholder="Enter description in English"
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* ========== Products Section ========== */}
            <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {isArabic ? "قسم المنتجات" : "Products Section"}
                </Typography>

                <Grid container spacing={3}>
                    {/* Products Section Title */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان قسم المنتجات (عربي)"
                                placeholder="أدخل عنوان قسم المنتجات بالعربي"
                                value={productsSectionTitleAr}
                                onChange={(e) => setProductsSectionTitleAr(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Products Section Title (English)"
                                placeholder="Enter products section title in English"
                                value={productsSectionTitleEn}
                                onChange={(e) => setProductsSectionTitleEn(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Products Section Description */}
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                وصف قسم المنتجات (عربي)
                            </Typography>
                            <TextEditor
                                name="products_section_description_ar"
                                value={productsSectionDescriptionAr}
                                onChange={(e) => setProductsSectionDescriptionAr(e.target.value)}
                                placeholder="أدخل وصف قسم المنتجات بالعربي"
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Products Section Description (English)
                            </Typography>
                            <TextEditor
                                name="products_section_description_en"
                                value={productsSectionDescriptionEn}
                                onChange={(e) => setProductsSectionDescriptionEn(e.target.value)}
                                placeholder="Enter products section description in English"
                            />
                        </Grid>
                    )}

                    {/* Products Images */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                            {isArabic ? "صور المنتجات" : "Product Images"}
                        </Typography>

                        {/* Existing Products (edit mode) */}
                        {products.filter(p => typeof p === 'object' && !deletedImages.includes(p.id)).map((product, index) => {
                            const productUrl = typeof product === 'object' ? product.url : product;
                            return (
                                <Box key={index} sx={{ position: 'relative', display: 'inline-block', mr: 2, mb: 2 }}>
                                    <FormInput
                                        type="file"
                                        name={`product_${index}`}
                                        labelImage={`${isArabic ? 'منتج' : 'Product'} ${index + 1}`}
                                        value={productUrl}
                                        onChange={() => { }}
                                        width={120}
                                        height={80}
                                        required={false}
                                    />
                                    <IconButton
                                        onClick={() => removeProduct(products.indexOf(product))}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'error.main',
                                            color: '#fff',
                                            '&:hover': { bgcolor: 'error.dark' },
                                            width: 24,
                                            height: 24,
                                            zIndex: 10,
                                        }}
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                            );
                        })}

                        {/* MultiImageUploader for new products */}
                        <Box sx={{ mt: 2 }}>
                            <FormInput
                                type="multi-image"
                                label={isArabic ? "إضافة صور منتجات جديدة" : "Add New Product Images"}
                                value={newProducts}
                                onChange={(updater) => {
                                    if (typeof updater === 'function') {
                                        setNewProducts(updater);
                                    }
                                }}
                                maxImages={10}
                                maxSizeMB={2}
                                required={false}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* ========== Features Section ========== */}
            <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6">
                        {isArabic ? "المميزات" : "Features"}
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={addFeature}
                        size="small"
                    >
                        {isArabic ? "إضافة ميزة" : "Add Feature"}
                    </Button>
                </Box>

                {features.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        {isArabic ? "لا توجد مميزات. اضغط على 'إضافة ميزة' لإضافة واحدة." : "No features yet. Click 'Add Feature' to add one."}
                    </Typography>
                )}

                {features.map((feature, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                        {index > 0 && <Divider sx={{ mb: 2 }} />}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {isArabic ? `ميزة ${index + 1}` : `Feature ${index + 1}`}
                            </Typography>
                            <IconButton
                                onClick={() => removeFeature(index)}
                                size="small"
                                color="error"
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Box>

                        <Grid container spacing={2}>
                            {/* Feature Image */}
                            <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <FormInput
                                    type="file"
                                    name={`feature_image_${index}`}
                                    labelImage={isArabic ? "صورة الميزة" : "Feature Image"}
                                    value={feature.image}
                                    onChange={(e) => handleFeatureImageChange(index, e)}
                                    width={100}
                                    height={80}
                                    required={false}
                                />
                            </Grid>

                            {/* Feature Title */}
                            <Grid item xs={12} sm={9}>
                                <Grid container spacing={2}>
                                    {activeLang === "ar" && (
                                        <Grid item xs={12} md={6}>
                                            <FormInput
                                                label={`عنوان الميزة ${index + 1} (عربي)`}
                                                placeholder="عنوان الميزة بالعربي"
                                                value={feature.titleAr}
                                                onChange={(e) => updateFeature(index, 'titleAr', e.target.value)}
                                                fullWidth
                                                required={false}
                                            />
                                        </Grid>
                                    )}

                                    {activeLang === "en" && (
                                        <Grid item xs={12} md={6}>
                                            <FormInput
                                                label={`Feature ${index + 1} Title (English)`}
                                                placeholder="Feature title in English"
                                                value={feature.titleEn}
                                                onChange={(e) => updateFeature(index, 'titleEn', e.target.value)}
                                                fullWidth
                                                required={false}
                                            />
                                        </Grid>
                                    )}

                                    {/* Feature Description */}
                                    {activeLang === "ar" && (
                                        <Grid item xs={12}>
                                            <FormInput
                                                type="textarea"
                                                label={`وصف الميزة ${index + 1} (عربي)`}
                                                placeholder="وصف الميزة بالعربي"
                                                value={feature.descriptionAr}
                                                onChange={(e) => updateFeature(index, 'descriptionAr', e.target.value)}
                                                fullWidth
                                                required={false}
                                            />
                                        </Grid>
                                    )}

                                    {activeLang === "en" && (
                                        <Grid item xs={12}>
                                            <FormInput
                                                type="textarea"
                                                label={`Feature ${index + 1} Description (English)`}
                                                placeholder="Feature description in English"
                                                value={feature.descriptionEn}
                                                onChange={(e) => updateFeature(index, 'descriptionEn', e.target.value)}
                                                fullWidth
                                                required={false}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Paper>

            {/* Submit Button */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={loadingSubmit}
                    sx={{ minWidth: 120 }}
                >
                    {loadingSubmit ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                </Button>
            </Box>
        </Box>
    );
};

export default FormAddEditAgency;
